import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { theme } from '../../styles/theme';
import { convertToVwWeb } from '../../Utils/responsive';
import kakaoModalImg from '../../assets/img/kakao_modal_img.png'; // Assuming this exists or I need to use a placeholder
import { authRequest } from '../../core/api/authApiClient';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { getUserDetails, updateUser } from '../../Module/User/Presentation/userActions';
import { selectUserProfile } from '../../Module/User/Presentation/userSelectors';
import { isIndividualUserType } from '../../Utils/userType';
import type { UpdateUserRequest } from '../../Module/User/Domain/entity/UserTypes';

// Declare Kakao on window
declare global {
  interface Window {
    Kakao: any;
  }
}

const KAKAO_KEY = 'd7fe97bd56b8e854e11feab6313e4b0a';
const REDIRECT_URI = 'https://on.missionfund.org/project';

interface KakaoChannelModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const KakaoChannelModal: React.FC<KakaoChannelModalProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation('translation');
  const dispatch = useAppDispatch();
  const userProfile = useAppSelector(selectUserProfile);
  const processedCodeRef = useRef<string>('');

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const params = new URLSearchParams(window.location.search);
    const code = params.get('code')?.trim();
    if (!code || processedCodeRef.current === code) return;
    processedCodeRef.current = code;

    const exchangeKakaoCode = async () => {
      try {
        const response = await authRequest<{
          result_Code?: number;
          result_code?: number;
          result_message?: string;
          statusCode?: number;
          data?: {
            kakaoId?: string | number;
            phoneNumber?: string;
            profileImage?: string;
            name?: string;
            username?: string;
            nickname?: string;
            sns_name?: string;
            birthdate?: string;
            birthyear?: string;
            birthday?: string;
            sns_birth?: string;
          };
        }>('/auth/kakao/token', {
          body: { code },
        });
        const resultCode = response?.result_Code ?? response?.result_code;
        if (
          (typeof response?.statusCode === 'number' && response.statusCode !== 200) ||
          (typeof resultCode === 'number' && resultCode !== 0)
        ) {
          throw new Error(response?.result_message || t('common.kakaoAuth.tokenExchangeFailed'));
        }

        const userId = window.localStorage.getItem('missionfund.userId');
        if (userId) {
          const kakaoData = response?.data;
          const kakaoIdText =
            kakaoData?.kakaoId === undefined || kakaoData?.kakaoId === null
              ? ''
              : String(kakaoData.kakaoId).trim();
          const sns_id = kakaoIdText
            ? kakaoIdText.startsWith('user')
              ? kakaoIdText
              : `user${kakaoIdText}`
            : undefined;
          const sns_phone =
            typeof kakaoData?.phoneNumber === 'string' && kakaoData.phoneNumber.trim()
              ? kakaoData.phoneNumber.trim()
              : undefined;
          const sns_thumbnail =
            typeof kakaoData?.profileImage === 'string' && kakaoData.profileImage.trim()
              ? kakaoData.profileImage.trim()
              : undefined;
          const sns_name =
            kakaoData?.sns_name ?? kakaoData?.name ?? kakaoData?.username ?? kakaoData?.nickname;
          const sns_birth =
            kakaoData?.sns_birth ??
            (kakaoData?.birthdate && /^\d{4}-\d{2}-\d{2}$/.test(kakaoData.birthdate)
              ? kakaoData.birthdate
              : undefined) ??
            (kakaoData?.birthyear && kakaoData?.birthday
              ? `${kakaoData.birthyear}-${String(kakaoData.birthday).padStart(4, '0').slice(0, 2)}-${String(kakaoData.birthday).padStart(4, '0').slice(2)}`
              : undefined);

          if (sns_id || sns_phone || sns_thumbnail || sns_name || sns_birth) {
            // POST /users/put 사용 (patch는 sns_id 미지원 가능성 있음)
            let profile = userProfile;
            if (!profile?.email || !profile?.partner) {
              try {
                profile = await dispatch(getUserDetails(userId)).unwrap();
              } catch (e) {
                console.error('Kakao auth: getUserDetails failed', e);
                processedCodeRef.current = '';
                alert(t('common.kakaoAuth.userLoadFailed'));
                return;
              }
            }
            if (!profile?.email) {
              processedCodeRef.current = '';
              alert(t('common.kakaoAuth.missingEmail'));
              return;
            }
            const updatePayload: UpdateUserRequest = {
              user_id: userId,
              email: profile!.email,
              partner: profile!.partner ?? 'OMF',
              sns_id,
              sns_phone,
              sns_thumbnail,
              ...(sns_name && { sns_name }),
              ...(sns_birth && { sns_birth }),
            };
            if (isIndividualUserType(profile?.user_type) && sns_phone) {
              updatePayload.phone = sns_phone;
            }

            const filteredRequest = Object.fromEntries(
              Object.entries(updatePayload).filter(([, value]) => value !== undefined)
            ) as UpdateUserRequest;

            try {
              await dispatch(updateUser(filteredRequest)).unwrap();
            } catch (err) {
              const msg = String((err as Error)?.message ?? '');
              const isSnsDup =
                msg.toLowerCase().includes('sns_id') && msg.toLowerCase().includes('duplicated');
              if (!isSnsDup) {
                alert(msg || t('common.kakaoAuth.saveFailed'));
                return;
              }
            }
          }

          dispatch(getUserDetails(userId));
        }

        params.delete('code');
        const next = params.toString();
        const nextUrl = `${window.location.pathname}${next ? `?${next}` : ''}${window.location.hash || ''}`;
        window.history.replaceState({}, '', nextUrl);
      } catch (error) {
        console.error('Failed to exchange Kakao auth code:', error);
      }
    };

    exchangeKakaoCode();
  }, [dispatch, t, userProfile]);

  useEffect(() => {
    if (!isOpen) return;

    const initializeKakao = () => {
      try {
        if (!window.Kakao) {
          console.error('Kakao SDK not loaded');
          return;
        }
        if (!window.Kakao.isInitialized()) {
          window.Kakao.init(KAKAO_KEY);
        }
      } catch (e) {
        console.error('Failed to initialize Kakao SDK:', e);
      }
    };

    if (window.Kakao) {
      initializeKakao();
    } else {
      const script = document.createElement('script');
      script.src = 'https://t1.kakaocdn.net/kakao_js_sdk/2.6.0/kakao.min.js';
      // Integrity check failed, so we remove it for now to allow loading
      // script.integrity = 'sha384-6MFdIr0zOira1CHQkedUqJVqe0ryQtcLs7ufRWw+T8rCXNKryCOgM/c7x5+z2JK7';
      script.crossOrigin = 'anonymous';
      script.onload = initializeKakao;
      script.onerror = (e) => console.error('Failed to load Kakao SDK script:', e);
      document.head.appendChild(script);
    }
  }, [isOpen]);

  const handleKakaoAuth = () => {
    if (!window.Kakao) {
      console.error('Kakao SDK not loaded');
      return;
    }

    if (!window.Kakao.isInitialized()) {
      window.Kakao.init(KAKAO_KEY);
    }

    if (window.Kakao.Auth) {
      // For demo/development purpose only:
      // Since we can't handle the actual callback on localhost without proper backend setup,
      // we'll simulate a successful authentication flow here.
      // alert('카카오 인증 페이지로 이동합니다.');
      
      window.Kakao.Auth.authorize({
        redirectUri: REDIRECT_URI,
      });
    } else {
      console.error('Kakao.Auth is not available');
    }
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <CloseButton onClick={onClose}>&times;</CloseButton>
        <ImageWrapper>
          <img src={kakaoModalImg} alt={t('common.kakaoAuth.modalAlt')} onError={(e) => e.currentTarget.style.display = 'none'} />
        </ImageWrapper>
        <TextWrapper>
          <MainText>{t('common.kakaoAuth.modalLine1')}</MainText>
          <MainText>{t('common.kakaoAuth.modalLine2')}</MainText>
        </TextWrapper>
        <KakaoButton onClick={handleKakaoAuth}>
          <IconWrapper>
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="19" viewBox="0 0 20 19" fill="none">
              <path d="M10 0C15.5228 0 20 3.44404 20 7.69238C19.9999 11.9407 15.5228 15.3848 10 15.3848C9.43635 15.3848 8.88392 15.3463 8.3457 15.2773L3.55273 18.874C3.39944 18.989 3.18767 18.8434 3.24023 18.6592L4.5332 14.1318C1.80412 12.7583 3.35915e-05 10.3884 0 7.69238C0 3.44404 4.47715 0 10 0Z" fill="#181600"/>
            </svg>
          </IconWrapper>
          {t('common.kakaoAuth.action')}
        </KakaoButton>
      </ModalContainer>
    </ModalOverlay>
  );
};

export default KakaoChannelModal;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
`;

const ModalContainer = styled.div`
  background-color: white;
  width: ${convertToVwWeb(400)};
  padding: ${convertToVwWeb(40)} ${convertToVwWeb(24)} ${convertToVwWeb(24)};
  border-radius: ${convertToVwWeb(16)};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${convertToVwWeb(12)};
  position: relative;
  box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.15);

  @media (max-width: 768px) {
    width: 300px;
    padding: 32px 20px 20px;
    border-radius: 16px;
    gap: 12px;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: ${convertToVwWeb(16)};
  right: ${convertToVwWeb(16)};
  background: none;
  border: none;
  font-size: ${convertToVwWeb(28)};
  color: ${theme.colors.grayScale[40]};
  cursor: pointer;
  line-height: 1;

  @media (max-width: 768px) {
    top: 16px;
    right: 16px;
    font-size: 24px;
  }
`;

const ImageWrapper = styled.div`
  width: ${convertToVwWeb(140)};
  height: ${convertToVwWeb(140)};
  
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  @media (max-width: 768px) {
    width: 120px;
    height: 120px;
    margin-bottom: -10px;
  }
`;

const TextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${convertToVwWeb(4)};
  margin-top: -${convertToVwWeb(12)};

  @media (max-width: 768px) {
    margin-top: 0;
    gap: 4px;
  }
`;

const MainText = styled.p`
  color: #17171B;
  text-align: center;
  font-family: Pretendard;
  font-size: 20px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 16px;
    line-height: 140%;
  }
`;

const KakaoButton = styled.button`
  width: 100%;
  height: ${convertToVwWeb(56)};
  background-color: #FEE500;
  border-radius: ${convertToVwWeb(12)};
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${convertToVwWeb(8)};
  cursor: pointer;
  
  ${theme.typography.contents.medium20};
  color: #191919;
  
  &:hover {
    background-color: #FDD835;
  }

  @media (max-width: 768px) {
    height: 48px;
    border-radius: 8px;
    font-size: 15px;
    font-weight: 500;
    margin-top: 8px;
  }
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;
